import React, { useState, useEffect } from 'react';
import { FiCalendar, FiMapPin, FiUsers, FiCheckCircle, FiAlertCircle, FiClock, FiInfo, FiArrowLeft, FiRefreshCw, FiArrowRight } from 'react-icons/fi';
import { FaTrain } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../components/Common/Card';
import Button from '../components/Common/Button';
import Shimmer from '../components/Common/Shimmer';
import apiService from '../services/api';
import { PNRStatus } from '../types';
import styles from './PNRResultPage.module.css';

const PNRResultPage: React.FC = () => {
  const { pnr } = useParams<{ pnr: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [pnrData, setPnrData] = useState<PNRStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (pnr) {
      fetchPNRData(pnr);
    } else {
      navigate('/pnr-status');
    }
  }, [pnr]);

  const fetchPNRData = async (pnrNumber: string) => {
    setIsLoading(true);
    setError(null);
    setPnrData(null);

    const cleanPNR = pnrNumber.trim().replace(/\D/g, '');
    if (cleanPNR.length !== 10) {
      setError('Invalid PNR number. Must be 10 digits.');
      setIsLoading(false);
      toast.error('Invalid PNR number');
      return;
    }

    try {
      const response = await apiService.getPNRStatus(cleanPNR);

      if (response.success && response.data) {
        setPnrData(response.data);
        toast.success('PNR status fetched successfully!');
      } else {
        setError(response.error || 'Failed to fetch PNR status');
        toast.error(response.error || 'Failed to fetch PNR status');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
      toast.error('An error occurred. Please try again.');
      console.error('PNR Status Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    if (pnr) {
      fetchPNRData(pnr);
    }
  };

  const handleNewSearch = () => {
    navigate('/pnr-status');
  };

  const getStatusColor = (status: string) => {
    const upperStatus = status.toUpperCase();
    if (upperStatus.includes('CNF') || upperStatus.includes('CONFIRM')) return styles.confirmed;
    if (upperStatus.includes('RAC')) return styles.rac;
    if (upperStatus.includes('WL') || upperStatus.includes('WAITING')) return styles.waitlist;
    return styles.default;
  };

  const getBerthIcon = (berthType: string) => {
    const berth = berthType.toUpperCase();
    if (berth.includes('LOWER') || berth === 'LB') return '⬇️';
    if (berth.includes('MIDDLE') || berth === 'MB') return '↔️';
    if (berth.includes('UPPER') || berth === 'UB') return '⬆️';
    if (berth.includes('SIDE') || berth.includes('SL') || berth.includes('SU')) return '↗️';
    return '🪑';
  };

  return (
    <div className={styles.pnrResultPage}>
      <div className="container">
        {/* Header with Actions */}
        {/* <div className={styles.header}>
          <div className={styles.headerActions}>
            <Button variant="outline" size="md" onClick={handleNewSearch}>
              <FiArrowLeft />
              New Search
            </Button>
            <Button variant="secondary" size="md" onClick={handleRefresh} isLoading={isLoading}>
              <FiRefreshCw />
              Refresh
            </Button>
          </div>
        </div> */}

        {/* Loading State */}
        {isLoading && (
          <div className={styles.resultsSection}>
            <Shimmer type="card" count={1} />
            <Shimmer type="table" count={1} />
          </div>
        )}

        {/* Error State */}
        {!isLoading && error && (
          <Card className={styles.errorCard}>
            <div className={styles.errorContent}>
              <FiAlertCircle className={styles.errorIcon} />
              <h3 className={styles.errorTitle}>Unable to Fetch PNR Status</h3>
              <p className={styles.errorMessage}>{error}</p>
              <div className={styles.errorActions}>
                <Button variant="primary" onClick={handleRefresh}>
                  Try Again
                </Button>
                <Button variant="outline" onClick={handleNewSearch}>
                  New Search
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Results - ConfirmTkt Style */}
        {!isLoading && !error && pnrData && (
          <div className={styles.resultsSection}>
            {/* PNR Header Card */}
            <Card className={styles.pnrHeaderCard}>
              <div className={styles.pnrHeaderTop}>
                <h2 className={styles.pnrTitle}>PNR : {pnrData.pnr}</h2>
                <button className={styles.changeButton} onClick={handleNewSearch}>
                  CHANGE
                </button>
              </div>

              {/* Train Info Section */}
              <div className={styles.trainInfoSection}>
                <div className={styles.trainHeader}>
                  <h3 className={styles.trainNumber}>
                    {pnrData.train.number} - {pnrData.train.name}
                  </h3>
                  <div className={styles.trainRating}>★ 4.1</div>
                </div>

                {/* Journey Route */}
                <div className={styles.journeyRoute}>
                  <div className={styles.routeStation}>
                    <span className={styles.stationName}>{pnrData.journey.from.name}</span>
                    <span className={styles.stationCode}>- {pnrData.journey.from.code}</span>
                    <span className={styles.stationTime}>, {pnrData.journey.departure}</span>
                  </div>
                  <FiArrowRight className={styles.routeArrow} />
                  <div className={styles.routeStation}>
                    <span className={styles.stationName}>{pnrData.journey.to.name}</span>
                    <span className={styles.stationCode}>- {pnrData.journey.to.code}</span>
                    <span className={styles.stationTime}>, {pnrData.journey.arrival}</span>
                  </div>
                </div>

                {/* Journey Meta Info */}
                <div className={styles.journeyMeta}>
                  <span>Sat, 15 Nov</span>
                  <span className={styles.metaSeparator}>|</span>
                  <span>{pnrData.train.class}</span>
                  <span className={styles.metaSeparator}>|</span>
                  <span>{pnrData.status}</span>
                  {pnrData.journey.from.platform && (
                    <>
                      <span className={styles.metaSeparator}>|</span>
                      <span>Expected platform: {pnrData.journey.from.platform}</span>
                    </>
                  )}
                </div>
              </div>
            </Card>

            {/* Passenger Status Table */}
            <Card className={styles.passengerStatusCard}>
              <div className={styles.passengerStatusHeader}>
                <h3 className={styles.passengerStatusTitle}>Passenger Status</h3>
                <div className={styles.chartStatusBadge}>
                  {pnrData.chart.status}
                  <button className={styles.refreshIconBtn} onClick={handleRefresh}>
                    <FiRefreshCw />
                  </button>
                </div>
              </div>

              {/* Passenger Table */}
              <div className={styles.passengerTable}>
                <div className={styles.tableHeader}>
                  <div className={styles.tableCol}>S. No</div>
                  <div className={styles.tableCol}>Current Status</div>
                  <div className={styles.tableCol}>Booking Status</div>
                  <div className={styles.tableCol}>Coach</div>
                </div>

                {pnrData.passengers && pnrData.passengers.length > 0 ? (
                  pnrData.passengers.map((passenger, index) => (
                    <div key={index} className={styles.tableRow}>
                      <div className={styles.tableCol}>
                        <span className={styles.passengerSNo}>{index + 1}</span>
                      </div>
                      <div className={styles.tableCol}>
                        <div className={styles.statusCell}>
                          <span className={`${styles.statusBadge} ${getStatusColor(passenger.status)}`}>
                            {passenger.status}
                          </span>
                          {passenger.status.toUpperCase().includes('CNF') && (
                            <span className={styles.availabilityTag}>Available</span>
                          )}
                        </div>
                      </div>
                      <div className={styles.tableCol}>
                        <span className={styles.bookingStatus}>{passenger.status}</span>
                      </div>
                      <div className={styles.tableCol}>
                        <span className={styles.coachNumber}>
                          {passenger.seat ? passenger.seat.split('/')[0] : 'N/A'}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.noPassengers}>No passenger information available</div>
                )}
              </div>
            </Card>

            {/* Additional Journey Details */}
            <Card className={styles.detailsCard}>
              <h3 className={styles.detailsTitle}>Journey Details</h3>
              <div className={styles.detailsGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>From Platform</span>
                  <span className={styles.detailValue}>{pnrData.journey.from.platform || 'TBA'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>To Platform</span>
                  <span className={styles.detailValue}>{pnrData.journey.to.platform || 'TBA'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Travel Duration</span>
                  <span className={styles.detailValue}>{pnrData.journey.duration || 'N/A'}</span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Class</span>
                  <span className={styles.detailValue}>{pnrData.train.class}</span>
                </div>
              </div>
            </Card>

            {/* Passenger Details (Detailed View) */}
            {pnrData.passengers && pnrData.passengers.length > 0 && (
              <Card className={styles.passengerDetailsCard}>
                <h3 className={styles.detailsTitle}>Detailed Passenger Information</h3>
                <div className={styles.passengerDetailsList}>
                  {pnrData.passengers.map((passenger, index) => (
                    <div key={index} className={styles.passengerDetailItem}>
                      <div className={styles.passengerDetailHeader}>
                        <span className={styles.passengerLabel}>Passenger {index + 1}</span>
                        {passenger.name && (
                          <span className={styles.passengerNameText}>{passenger.name}</span>
                        )}
                      </div>
                      <div className={styles.passengerDetailGrid}>
                        <div className={styles.detailItem}>
                          <span className={styles.detailLabel}>Seat Number</span>
                          <span className={styles.detailValue}>{passenger.seat || 'Not Assigned'}</span>
                        </div>
                        <div className={styles.detailItem}>
                          <span className={styles.detailLabel}>Berth Type</span>
                          <span className={styles.detailValue}>
                            {getBerthIcon(passenger.berthType)} {passenger.berthType}
                          </span>
                        </div>
                        {passenger.confirmationProbability && (
                          <div className={styles.detailItem}>
                            <span className={styles.detailLabel}>Confirmation Probability</span>
                            <span className={styles.detailValue}>{passenger.confirmationProbability}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Last Updated */}
            {pnrData.lastUpdated && (
              <div className={styles.lastUpdated}>
                <FiInfo />
                <span>Last Updated: {pnrData.lastUpdated}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PNRResultPage;